import type { Match, Team } from '@uefadigital/panenka';
import i18n from 'i18next';

import type { CompstatsV1TeamStatisticsHead2Head, Statistics } from 'types/ApiResponseInterfaces';
import config from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

export const getTeamId = (team: Team | undefined) => {
  return team ? team.id : '';
};

export const getConnectedCompetitions = (competitionId: string) => {
  const connectedCompetitions: Array<string> = [competitionId];

  config.connectedCompetition.map((competitions) => {
    if (!competitions.includes(competitionId)) {
      return;
    }

    competitions.map((competitionId) => {
      connectedCompetitions.push(competitionId);
    });
  });

  return connectedCompetitions;
};

export const getMatchResult = (match: Match) => {
  const homeTeam = translateEntity(match?.homeTeam)?.displayName;
  const awayTeam = translateEntity(match?.awayTeam)?.displayName;
  const score = `${match?.score?.total?.home ?? 0} - ${match?.score?.total?.away ?? 0}`;
  const competition = translateEntity(match?.competition)?.name;

  const start = new Date(match?.kickOffTime?.date ?? '');
  const date = start.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return {
    homeTeam,
    awayTeam,
    score,
    competition,
    date,
  };
};

export const getMatchResultDescription = (match: Match) => {
  const { homeTeam, awayTeam, score, competition, date } = getMatchResult(match);

  const primary = `${homeTeam} ${score} ${awayTeam}`;
  const secondary = `${date} • ${competition}`;

  return { primary, secondary };
};

export interface MatchesInfo {
  title: string;
  value: string;
}

export const getMatchStatistics = (
  headToHead: CompstatsV1TeamStatisticsHead2Head[] | undefined,
  teamId: string,
  statisticsName: string,
): MatchesInfo => {
  const stats = headToHead?.filter(function (item: CompstatsV1TeamStatisticsHead2Head) {
    return item.teamId === teamId;
  });

  if (!stats || !stats[0] || !stats[0]['statistics']) {
    return { title: '', value: '0' };
  }

  const matches = stats[0]['statistics'].filter(function (item: Statistics) {
    return item.name === statisticsName;
  });

  if (!matches || !matches[0]) {
    return { title: '', value: '0' };
  }

  return {
    title: translateEntity(matches[0]).name,
    value: matches[0].value,
  };
};

export const getOverallStatistics = (
  headToHead: CompstatsV1TeamStatisticsHead2Head[] | undefined,
  matchData: Match,
) => {
  const homeWin = getMatchStatistics(headToHead, getTeamId(matchData?.homeTeam), 'matches_win');
  const draw = getMatchStatistics(headToHead, getTeamId(matchData?.homeTeam), 'matches_draw');
  const awayWin = getMatchStatistics(headToHead, getTeamId(matchData?.awayTeam), 'matches_win');

  return { homeWin, draw, awayWin };
};

export const splitH2HMatches = (H2HMatches: Match[]) => {
  const firstHalf = [];
  const secondHalf = [];

  for (let i = 0; i < H2HMatches.length; i++) {
    if (i % 2 === 0) {
      firstHalf.push(H2HMatches[i]);
    } else {
      secondHalf.push(H2HMatches[i]);
    }
  }

  return { firstHalf, secondHalf };
};
